import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import { rolesApi } from '@/services/api';
import type { Role, Scope } from '@/types/roles';
import ScopeGrid from './ScopeGrid';
import FormCard, { FormSection, FormField, FormInput } from '@/components/utils/FormCard';

const RoleForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  const [roleName, setRoleName] = useState('');
  const [selectedScopeIds, setSelectedScopeIds] = useState<number[]>([]);
  const [allScopes, setAllScopes] = useState<Scope[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadingData(true);

    const scopesPromise = rolesApi.getScopes();
    const rolePromise = isEditMode && id
      ? rolesApi.getRole(Number(id))
      : Promise.resolve(null);

    Promise.all([scopesPromise, rolePromise])
      .then(([scopes, role]) => {
        if (cancelled) return;
        setAllScopes(scopes);
        if (role) {
          setRoleName(role.name);
          setSelectedScopeIds(role.scopes.map((s) => s.id));
        }
      })
      .catch(() => {
        if (!cancelled) {
          toast.error(isEditMode ? 'Failed to load role details.' : 'Failed to load scopes.');
          if (isEditMode) navigate('/roles-management');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingData(false);
      });

    return () => { cancelled = true; };
  }, [id, isEditMode, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = roleName.trim();

    if (!trimmedName) {
      toast.error('Role name is required.');
      return;
    }

    setIsSaving(true);
    try {
      let savedRole: Role;

      if (isEditMode && id) {
        savedRole = await rolesApi.updateRole(Number(id), { name: trimmedName });
        await rolesApi.setRoleScopes(savedRole.id, selectedScopeIds);
        toast.success(`Role "${savedRole.name}" updated.`);
      } else {
        savedRole = await rolesApi.createRole({ name: trimmedName });
        await rolesApi.setRoleScopes(savedRole.id, selectedScopeIds);
        toast.success(`Role "${savedRole.name}" created.`);
      }
      navigate('/roles-management');
    } catch (error: unknown) {
      const message = axios.isAxiosError(error)
        ? (error.response?.data?.name?.[0] ?? error.response?.data?.detail ?? 'Failed to save role')
        : 'Failed to save role';
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const submitLabel = isSaving ? 'Saving…' : (isEditMode ? 'Save Changes' : 'Create Role');

  return (
    <FormCard
      title={isEditMode ? 'Edit Role' : 'New Role'}
      subtitle={isEditMode
        ? 'Edit the role name and permissions below.'
        : 'Create a role and assign permissions below.'}
      backLabel="← Back to Roles"
      onBack={() => navigate('/roles-management')}
      submitLabel={submitLabel}
      onSubmit={handleSubmit}
      onCancel={() => navigate('/roles-management')}
      submitting={isSaving}
      loading={loadingData}
      loadingMessage="Loading role details…"
      variant="page"
    >
      <FormSection title="Role Name">
        <FormField label="Name" required maxWidth="400px">
          <FormInput
            type="text"
            value={roleName}
            onChange={(e) => setRoleName(e.target.value)}
            placeholder="e.g. Trader"
            disabled={isSaving}
          />
        </FormField>
      </FormSection>

      <FormSection title={`Permissions (${selectedScopeIds.length} selected)`}>
        <ScopeGrid
          allScopes={allScopes}
          selectedIds={selectedScopeIds}
          onChange={setSelectedScopeIds}
          disabled={isSaving}
        />
      </FormSection>
    </FormCard>
  );
};

export default RoleForm;